import { useAppShell, useIsMobile, type BoxProps } from "@salestrak/ui";

/** The Box props that make a sidebar pane draggable on its right edge. */
type SidebarResizeProps = Pick<
  BoxProps,
  | "maxWidth"
  | "minWidth"
  | "onResize"
  | "onResizeEnd"
  | "onResizeStart"
  | "resize"
  | "resizeLabel"
>;

/**
 * Spread onto the root Box of whatever fills the sidebar pane (the module menu,
 * the settings sidebar) to make that pane drag-resizable from its right edge.
 *
 * The pane owns the affordance rather than the shell, so whichever pane is
 * mounted carries its own seam. Width lives in the shell context — the
 * workspace layout persists it — and the drag flag drops the shell's width
 * transition for the gesture so the pane tracks the pointer instead of easing
 * behind it.
 *
 * Desktop only: on mobile the pane fills a drawer and has no edge to drag.
 */
export function useSidebarResize(): SidebarResizeProps {
  const isMobile = useIsMobile();
  const {
    maxSidebarWidth,
    minSidebarWidth,
    setResizingSidebar,
    setSidebarWidth,
  } = useAppShell();

  if (isMobile) return {};

  return {
    maxWidth: maxSidebarWidth,
    minWidth: minSidebarWidth,
    onResize: ({ width }) => setSidebarWidth(width),
    onResizeEnd: () => setResizingSidebar(false),
    onResizeStart: () => setResizingSidebar(true),
    resize: "right",
    resizeLabel: "Resize sidebar",
  };
}
